/**
 * Rebindable control map. (§4.1 + house control scheme, user decisions 2026-07-17)
 *
 * The house scheme OVERRIDES the brief's default mouse mappings:
 *   - LEFT click hold  = shoot (charge = hold; lateral mouse -> curl)
 *   - RIGHT click hold = pass  (charge)
 *   - P = punch player, K = kick player (arcade house-rule fouls)
 * House controls v2 (2026-07-17, "fix the controls"):
 *   - Space = JUMP (headers/aerial duels — InputFlags.Jump)
 *   - Q     = through ball (moved OFF Space, where it collided with jump)
 * House controls v3 (2026-07-18, LIVE FOOTBALL POLISH §6 remap):
 *   - F = slide tackle / first touch (moved off E)
 *   - bicycle-kick modifier (HOLD + recent jump + shoot release composes
 *         InputFlags.BicycleKick — see InputManager.ts's bicycleComposeActive).
 * Current match controls (2026-07-31 master controls pass):
 *   - C = switch player on tap / expand radar on hold. This uses the existing
 *         `radar` dual-action seam, so online authority and offline practice
 *         make the same selection they previously made on Tab.
 *   - V = cameraCycle. Bicycle remains on B; every action stays rebindable.
 *   - X = callForPass — a keyboard binding alongside the middle-mouse verb.
 *   - E = skill modifier (moved off F) — held while LMB releases a shot,
 *         this drives InputFlags.SkillModifier, which ball.ts's buildShot
 *         already reads as Finesse. Also still arms directional skill moves
 *         (BallRoll/DragBack/BodyFeint*) per its existing dual role.
 *   - R = loft/cross modifier (NEW action `loftModifier`) — held while RMB
 *         releases a pass, this composes InputFlags.ThroughBall onto that
 *         SAME pass release (see InputManager.ts's loftComposeActive), which
 *         ball.ts's buildPass already treats as "lofted, or a cross from wide
 *         + advanced areas". Deliberately NOT a per-tick held flag like
 *         SkillModifier: ThroughBall sits in rules/fsm.ts's TAKE_MASK (a
 *         restart "does this input want to play" test), so it must never be
 *         live on a tick without an actual Pass release, or holding R alone
 *         during a dead-ball wait would look like the taker committed.
 * Everything is a data map so it can be rebound live from the settings screen —
 * this file holds the DEFAULTS, the (de)serialization/validation helpers, and
 * the conflict test the rebind UI relies on. The UI never invents key names:
 * it reads REBINDABLE_ACTIONS + ACTION_LABELS, so what it shows is exactly
 * what the InputState will act on.
 *
 * Mouse-button rebind phase (2026-07-18, playtest defects #11/#12 — "LMB must
 * shoot", "RMB must pass"): mouse buttons used to live in a separate
 * `mouse: {shoot,pass,callForPass}` side-table of raw button numbers, which
 * meant they were NOT reachable through bind/unbind/keysForAction/keyLabel and
 * therefore not actually rebindable through this system (only editable by
 * hand-patching a settings blob). Mouse buttons are now ordinary entries in
 * `keys`, addressed by a synthetic `MouseN` code (`mouseCode(button)`, where N
 * is `MouseEvent.button`) — the mouse-button equivalent of
 * `KeyboardEvent.code`. One map, one rebind path, for both input devices.
 */

/** Every discrete, bindable action. Movement is four separate keys so WASD can
 *  be rebound individually; 'shoot'/'pass'/'callForPass' are the mouse verbs,
 *  bound to synthetic mouse codes by default (see `mouseCode`/`defaultControlMap`). */
export type BindableAction =
  | 'moveForward'
  | 'moveBack'
  | 'moveLeft'
  | 'moveRight'
  | 'shoot'
  | 'pass'
  | 'callForPass'
  | 'sprint'
  | 'boost'
  // `powerSlot1..4` (Mythic 5v5, 2026-08-16) are GONE with the mode. They are
  // deliberately no longer bindable actions: `isBindableAction` drops them on
  // parse, which is what frees Digit1 for the boost migration below.
  | 'walk'
  | 'jump'
  | 'throughBall'
  | 'bicycleModifier'
  | 'skillModifier'
  | 'loftModifier'
  | 'slideOrFirstTouch'
  | 'standingOrJockey'
  | 'punch'
  | 'kick'
  | 'radar'
  | 'liftUp'
  | 'liftDown'
  | 'tactic1'
  | 'tactic2'
  | 'tactic3'
  | 'tactic4'
  | 'cameraCycle'
  | 'menu';

/**
 * A `MouseEvent.button` (0 left / LMB, 1 middle / MMB, 2 right / RMB, 3/4 the
 * browser back/forward buttons) rendered as a synthetic bindable code — the
 * mouse equivalent of `KeyboardEvent.code`. Letting mouse buttons be ordinary
 * `keys` entries means bind/unbind/keysForAction/keyLabel/conflictForKey need
 * no mouse-specific branches at all.
 */
export function mouseCode(button: number): string {
  return `Mouse${button}`;
}

export interface ControlMap {
  /**
   * code -> action. `code` is either a `KeyboardEvent.code` (not `.key`, so
   * layout is stable) or a synthetic mouse code from `mouseCode()` — one map
   * for both input devices.
   */
  keys: Record<string, BindableAction>;
}

/** A fresh copy of the default house control scheme (v2). */
export function defaultControlMap(): ControlMap {
  return {
    keys: {
      KeyW: 'moveForward',
      KeyS: 'moveBack',
      KeyA: 'moveLeft',
      KeyD: 'moveRight',
      // House mouse verbs (defects #11/#12): LMB shoot, RMB pass, MMB call-
      // for-pass. Synthetic codes (see `mouseCode`) — same map, same rebind
      // path as every keyboard action below.
      Mouse0: 'shoot',
      Mouse2: 'pass',
      Mouse1: 'callForPass',
      ShiftLeft: 'sprint',
      ShiftRight: 'sprint',
      // WALK. Without this a keyboard player has exactly three speeds -
      // standing, jog, sprint - because WASD always produces a UNIT vector and
      // the sim reads its magnitude as the throttle. Measured over a goal-to-
      // goal traverse: 3 samples of 1235 fell in the Walk band [1.20, 2.00] m/s,
      // 0.2 % of the time spent moving, so the walk cycle was effectively
      // unreachable. A gamepad never had the problem - a half-pressed stick
      // already walks.
      //
      // ⚠ NOT Alt. Alt was the first choice and it never reaches the game: the
      // browser claims it for its own menu handling, so the keydown is either
      // swallowed or arrives without ever setting the held state. Tested in the
      // running game - the player kept sprinting with Alt+W held. Z is free,
      // sits under the left hand already on WASD, and is nobody's browser
      // shortcut.
      KeyZ: 'walk',
      Space: 'jump',
      // Q IS A MODIFIER, NOT A TRIGGER (owner, 2026-08-15: "dont kick the
      // ball when q is pressed - kick or pass the ball when q + mouse click
      // is done so the ball goes from above"). Held Q + LMB lofts a SHOT over
      // the keeper; held Q + RMB lofts the PASS. It joins R on the same
      // `loftModifier` action, so both keys do the same job and either can be
      // rebound. (Briefly the Mythic power key on 2026-08-16; the owner moved
      // powers to the number row the same day — "Restore Q to whatever normal
      // football binding it previously had" — so this is that binding, back.)
      KeyQ: 'loftModifier',
      // House controls v4 (DEFENSIVE REMAP §6): F = HOOK / drag standing tackle
      // (+ jockey on hold), E = POKE tackle on the spot / SLIDE at pace (+ first
      // touch when you have the ball). The skill modifier moved to Ctrl (freed by
      // the standing tackle moving to F). R = loft/cross.
      //
      // Current controls: C is the primary switch-player tap (and radar hold),
      // with Tab retained as an alternate; V cycles the camera. B remains the
      // bicycle modifier and X remains the keyboard call-for-pass binding.
      KeyF: 'standingOrJockey',
      KeyC: 'radar',
      KeyV: 'cameraCycle',
      KeyE: 'slideOrFirstTouch',
      KeyR: 'loftModifier',
      KeyX: 'callForPass',
      ControlLeft: 'skillModifier',
      ControlRight: 'skillModifier',
      KeyP: 'punch',
      KeyK: 'kick',
      Tab: 'radar',
      // THE BOOST IS ON 1 (owner, 2026-08-13: "when the user press button 1").
      // It was briefly pushed to Digit5 on 2026-08-16 to make room for the
      // Mythic 5v5 power slots on 1-4; that mode is removed, the slots with it,
      // and boost is back where it was asked for. Owner, 2026-08-17: "was boost
      // removed?" — it was not, it was on the wrong key. The tactical set moved
      // off 1-4 to F1-F4 for the boost back on 2026-08-13 and stays there.
      Digit1: 'boost',
      F1: 'tactic1',
      F2: 'tactic2',
      F3: 'tactic3',
      F4: 'tactic4',
      KeyB: 'bicycleModifier',
      Escape: 'menu',
    },
  };
}

/** Rebind a key. Any prior binding of the same code is replaced. Returns a new map. */
export function rebindKey(map: ControlMap, code: string, action: BindableAction): ControlMap {
  return { keys: { ...map.keys, [code]: action } };
}

/** Remove a key's binding entirely. Returns a new map. */
export function unbindKey(map: ControlMap, code: string): ControlMap {
  const keys = { ...map.keys };
  delete keys[code];
  return { keys };
}

/** Reverse lookup: is `code` currently bound to `action`? */
export function isBound(map: ControlMap, code: string, action: BindableAction): boolean {
  return map.keys[code] === action;
}

/** All codes currently bound to `action` (an action may have several — both Shifts). */
export function keysForAction(map: ControlMap, action: BindableAction): string[] {
  return Object.keys(map.keys).filter((code) => map.keys[code] === action);
}

/**
 * The rebind-UI conflict test: assigning `code` to `action` when it already
 * drives a DIFFERENT action is a conflict — the UI highlights the loser and
 * refuses the save rather than silently stealing the key. Returns the action
 * `code` currently belongs to, or null when the assignment is clean.
 */
export function conflictForKey(
  map: ControlMap,
  code: string,
  action: BindableAction,
): BindableAction | null {
  const existing = map.keys[code];
  return existing !== undefined && existing !== action ? existing : null;
}

/**
 * Actions the settings screen offers for rebinding, in display order.
 * Deliberately excluded:
 *   - 'menu' (Escape) — rebinding pause away can strand the player mid-match.
 *   - 'liftUp'/'liftDown' — InputState has no handler for them (lift bias is
 *     the mouse wheel); offering a bind that does nothing would be a lie.
 */
export const REBINDABLE_ACTIONS: readonly BindableAction[] = [
  'moveForward',
  'moveBack',
  'moveLeft',
  'moveRight',
  'shoot',
  'pass',
  'callForPass',
  'sprint',
  'boost',
  'walk',
  'jump',
  // 'throughBall' RETIRED 2026-08-15: Q became the loft MODIFIER (owner:
  // "dont kick the ball when q is pressed"), so no key fires a bare through
  // ball any more. The action stays in BindableAction + ALL_ACTIONS so a
  // stored blob naming it still deserialises; it is simply not offered.
  'bicycleModifier',
  'skillModifier',
  'loftModifier',
  'slideOrFirstTouch',
  'standingOrJockey',
  'punch',
  'kick',
  'radar',
  'cameraCycle',
  'tactic1',
  'tactic2',
  'tactic3',
  'tactic4',
];

/** Human-readable labels. Exhaustive on purpose — a new action cannot ship
 *  without deciding what the settings screen calls it. */
export const ACTION_LABELS: Record<BindableAction, string> = {
  moveForward: 'Move forward',
  moveBack: 'Move back',
  moveLeft: 'Move left',
  moveRight: 'Move right',
  shoot: 'Shoot (hold to charge, mouse sideways = curl)',
  pass: 'Pass (hold to charge)',
  callForPass: 'Call for pass',
  sprint: 'Sprint (hold)',
  boost: 'Stamina boost (tap; tap again for more)',
  walk: 'Walk (hold)',
  jump: 'Jump / header',
  throughBall: 'Through ball',
  bicycleModifier: 'Bicycle kick (hold + jump + shoot)',
  skillModifier: 'Skill / finesse (hold+dir: roll/drag/feint · +Sprint: knock-run/rabona-fake/step-over/elastico · +shoot: finesse)',
  loftModifier: 'Lofted pass / cross modifier (hold + pass)',
  slideOrFirstTouch: 'Poke tackle / slide at pace / first touch',
  standingOrJockey: 'Hook tackle / jockey (hold)',
  punch: 'Punch (always a foul)',
  kick: 'Kick player (always a foul)',
  radar: 'Switch player (tap) / radar (hold)',
  liftUp: 'Lift bias up',
  liftDown: 'Lift bias down',
  tactic1: 'Tactic: press high',
  tactic2: 'Tactic: drop deep',
  tactic3: 'Tactic: offside trap',
  tactic4: 'Tactic: man-mark',
  cameraCycle: 'Cycle camera',
  menu: 'Pause / menu',
};

/** Pretty label for a KeyboardEvent.code ("KeyW" → "W", "ShiftLeft" → "L-Shift")
 *  or a synthetic mouse code from `mouseCode()` ("Mouse0" → "LMB"). */
export function keyLabel(code: string): string {
  if (code.startsWith('Mouse')) {
    const mouseNamed: Record<string, string> = { Mouse0: 'LMB', Mouse1: 'MMB', Mouse2: 'RMB' };
    return mouseNamed[code] ?? `Mouse ${code.slice(5)}`;
  }
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  const named: Record<string, string> = {
    Space: 'Space',
    ShiftLeft: 'L-Shift',
    ShiftRight: 'R-Shift',
    ControlLeft: 'L-Ctrl',
    ControlRight: 'R-Ctrl',
    AltLeft: 'L-Alt',
    AltRight: 'R-Alt',
    Tab: 'Tab',
    Escape: 'Esc',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Backquote: '`',
    Minus: '-',
    Equal: '=',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
    Backslash: '\\',
    BracketLeft: '[',
    BracketRight: ']',
    Enter: 'Enter',
    Backspace: 'Bksp',
    CapsLock: 'Caps',
  };
  return named[code] ?? code;
}

// ---------------------------------------------------------------------------
// (De)serialization — the settings blob carries a ControlMap; never trust it.
// ---------------------------------------------------------------------------

const ALL_ACTIONS: readonly BindableAction[] = [
  'moveForward', 'moveBack', 'moveLeft', 'moveRight', 'shoot', 'pass',
  'callForPass', 'sprint', 'boost', 'walk', 'jump',
  'throughBall', 'bicycleModifier', 'skillModifier', 'loftModifier', 'slideOrFirstTouch',
  'standingOrJockey', 'punch', 'kick', 'radar', 'liftUp', 'liftDown',
  'tactic1', 'tactic2', 'tactic3', 'tactic4', 'cameraCycle', 'menu',
];

function isBindableAction(v: unknown): v is BindableAction {
  return typeof v === 'string' && (ALL_ACTIONS as readonly string[]).includes(v);
}

/**
 * Validate an untrusted (parsed-JSON) value into a ControlMap. Unknown actions
 * and non-string keys are dropped; a missing/most-broken map falls back to the
 * defaults wholesale — half a control scheme is worse than the stock one.
 */
export function parseControlMap(v: unknown): ControlMap {
  const fallback = defaultControlMap();
  if (typeof v !== 'object' || v === null) return fallback;
  const raw = v as { keys?: unknown };
  if (typeof raw.keys !== 'object' || raw.keys === null) return fallback;

  const keys: Record<string, BindableAction> = {};
  let bound = 0;
  for (const [code, action] of Object.entries(raw.keys as Record<string, unknown>)) {
    if (typeof code !== 'string' || code.length === 0 || code.length > 32) continue;
    if (!isBindableAction(action)) continue;
    // ⚠ MIGRATION: 'throughBall' RETIRED 2026-08-15. Q used to FIRE a through
    // ball on keydown; it is the loft MODIFIER now (owner: "dont kick the ball
    // when q is pressed ... q + mouse click ... so the ball goes from above").
    // A stored blob overrides the defaults WHOLE - this file's own scar - so
    // without this line a returning player's Q stayed bound to an action
    // nothing handles any more: pressing Q did nothing AND Q+click lofted
    // nothing, because his map had no loftModifier on Q. Measured in the
    // owner's running game. Whatever key carried the old trigger becomes the
    // new modifier, which is what that key MEANT to the player.
    keys[code] = action === 'throughBall' ? 'loftModifier' : action;
    bound++;
  }
  // A map that lost its movement keys is unplayable — take the defaults.
  const hasMovement =
    Object.values(keys).includes('moveForward') && Object.values(keys).includes('moveBack');
  if (bound < 4 || !hasMovement) return fallback;

  // ⚠ BACKFILL EVERY ACTION THE BLOB HAS NO BINDING FOR, NOT JUST THREE.
  //
  // This used to name shoot/pass/callForPass explicitly, because a blob saved
  // before mouse buttons joined this map would leave the player unable to act.
  // The same hole swallows every binding added afterwards: the map is persisted
  // WHOLE, so a stored blob silently overrides the defaults and an action that
  // did not exist when it was written can never reach that player. `walk` was
  // added on 2026-08-13 and did nothing in a browser with saved settings —
  // measured in the running game, the player kept sprinting with the walk key
  // held, and the binding was correct in the source the whole time.
  //
  // The rule is unchanged and is what makes this safe: only ever onto a code the
  // parsed map has not already claimed, so a deliberate rebind is never stolen
  // (the same rule the settings-screen conflict UI enforces). The cost is that
  // an action a player deliberately UNBOUND comes back on its default key if
  // that key is free — which is the lesser of the two evils, and visible in the
  // settings screen where they can unbind it again.
  // ⚠ THE BACKFILL ALONE IS NOT ENOUGH WHEN THE DEFAULT KEY IS ALREADY TAKEN.
  // `boost` (2026-08-13) defaults to Digit1, which every stored blob written
  // before it existed had assigned to `tactic1`. The loop below only ever claims
  // a FREE code, so boost would land on nothing at all and the feature would be
  // unreachable for exactly the players who have played before — measured in the
  // running game: Digit1 still read `tactic1` and `boost` had no binding.
  //
  // So the tactical set is moved off 1-4 to F1-F4 FIRST, and only on the exact
  // stock signature (all four still on the digits, function row untouched), so a
  // custom map that deliberately moved either group is left alone.
  const stockTactics =
    keys.Digit1 === 'tactic1' && keys.Digit2 === 'tactic2'
    && keys.Digit3 === 'tactic3' && keys.Digit4 === 'tactic4'
    && !Object.values(keys).includes('boost')
    && keys.F1 === undefined && keys.F2 === undefined
    && keys.F3 === undefined && keys.F4 === undefined;
  if (stockTactics) {
    delete keys.Digit1; delete keys.Digit2; delete keys.Digit3; delete keys.Digit4;
    keys.F1 = 'tactic1'; keys.F2 = 'tactic2'; keys.F3 = 'tactic3'; keys.F4 = 'tactic4';
  }
  // BOOST BACK TO 1 after the removed Mythic 5v5 mode (2026-08-16 → 17). For a
  // day the migration ABOVE this comment did the opposite: it moved every
  // returning player's boost from Digit1 to Digit5 to make room for the power
  // slots. The mode is gone and `powerSlot1..4` are no longer bindable, so
  // `isBindableAction` has already dropped them on parse — leaving Digit1 EMPTY
  // and boost stranded on Digit5, where nobody looks for it (owner, 2026-08-17:
  // "was boost removed?"). On exactly that stranded signature — Digit1 free,
  // boost on Digit5 — put it back. A player who DELIBERATELY put something
  // else on Digit1 and boost on 5 has a non-empty Digit1 and is left alone.
  if (keys.Digit1 === undefined && keys.Digit5 === 'boost') {
    keys.Digit1 = 'boost';
    delete keys.Digit5;
  }
  // Q → loft RESTORE (2026-08-16, same day): for about an hour Q was bound to
  // a `power` action that no longer exists. `isBindableAction` above drops that
  // stale name on parse, leaving Q EMPTY — and the backfill will not refill it,
  // because loft is already bound (on R) and backfill only places UNBOUND
  // actions. Q and R are a designed PAIR on loft, so an empty Q beside a
  // loft-R is put back ("Restore Q to whatever normal football binding it
  // previously had"). A Q the player rebound to something else is untouched.
  if (keys.KeyQ === undefined && keys.KeyR === 'loftModifier') {
    keys.KeyQ = 'loftModifier';
  }

  for (const action of ALL_ACTIONS) {
    if (Object.values(keys).includes(action)) continue;
    for (const code of keysForAction(fallback, action)) {
      if (keys[code] === undefined) {
        keys[code] = action;
        break;
      }
    }
  }

  // Migrate the immediately preceding stock layout (C=camera, Tab=switch) to
  // the current brief. The exact signature avoids rewriting a deliberate
  // custom map that already assigned V or moved either action elsewhere.
  if (keys['KeyC'] === 'cameraCycle' && keys['Tab'] === 'radar' && keys['KeyV'] === undefined) {
    keys['KeyC'] = 'radar';
    keys['KeyV'] = 'cameraCycle';
  }

  return { keys };
}
